import { ISiteDTO } from "@/Administration/Api/Dto/Sites/ISiteDTO";
import { AdministrationHelper } from "@/Administration/Helpers/AdministrationHelper";
import { ISiteCreateModel } from "@/Administration/Models/UI/Sites/ISiteCreateModel";
import { Address } from "@/Common/Api/Contacts/Dto/Address";
import { Email } from "@/Common/Api/Contacts/Dto/Email";
import { PhoneNumber } from "@/Common/Api/Contacts/Dto/PhoneNumber";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import ButtonGroup from "@/Common/Components/Buttons/ButtonGroup";
import { GuidHelper } from "@/Common/Helpers/GuidHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { ValidationHelper } from "@/Common/Helpers/ValidationHelper";
import { ContactPointType } from "@/Common/Models/Contacts/Enums/ContactPointType";
import { PhoneType } from "@/Common/Models/Contacts/Enums/PhoneType";
import { LocalizedTextDto } from "@/Common/Models/LocalizedTextDto";
import { OpeningHour } from "@/Common/Models/Sites/OpeningHour";
import { Site } from "@/Common/Models/Sites/ResponseModels/Site";
import { SitesModel } from "@/Common/Models/Sites/SitesModel";
import { SiteSpecialtyId } from "@/Common/Models/Sites/SiteSpecialtyId";
import * as LocalOperations from "@/Common/Operations/operations.local";
import AddressSection from "./SiteCreate/AddressSection";
import EmailSection from "./SiteCreate/EmailSection";
import InfoInPortalSection from "./SiteCreate/InfoInPortalSection";
import KioskInstructionSection from "./SiteCreate/KioskInstructionSection";
import PhoneSection from "./SiteCreate/PhoneSection";
import SecuritySection from "./SiteCreate/SecuritySection";
import SiteInfoSection from "./SiteCreate/SiteInfoSection";
import TaxesSection from "./SiteCreate/TaxesSection";

enum SiteGmfType {
    Gmf,
    Gmfr,
    Gmfu
}

interface ISiteCreateProps {
    model: ISiteCreateModel;
    titleLabel: string;
    infoLabel: string;
    selectedSite: Site;
}

class SiteCreateState {
    public id: string;

    public clinicalName: string;
    public clinicalCode: string;
    public selectAsMainEnrollmentForClinicalCode: boolean;
    public aipLogin: string;
    public acronym: string;
    public ceLoginRAMQ: string;
    public payeeNumber: string;
    public mspFacilityNumber: string;

    public numberBadAccess: number;
    public multifactorAuthenticationNumberBadAccess: number;
    public periodOfInactivityDays: number;
    public unapprovedNoteExpiryInMinutes: number;
    public unapprovedNoteMinutes: number;
    public unapprovedNoteHours: number;
    public unapprovedNoteDays: number;

    public isGmf: boolean;
    public isGmfu: boolean;
    public isGmfr: boolean;
    public mspLocationCode: string;

    public gst: number;
    public qst: number;
    public hst: number;
    public genericTax: number;

    public addresses: Address[];
    public phones: PhoneNumber[];
    public emails: Email[];
    public localizedDisplayName: LocalizedTextDto;
    public specialties: SiteSpecialtyId[];
    public offeredSpecialtyIds: string[];
    public localizedWebsite: LocalizedTextDto;
    public openingHours: OpeningHour[];
    public localizedDescription: LocalizedTextDto;
    public localizedOpeningHoursNote: LocalizedTextDto;
    public localizedKioskInstruction: LocalizedTextDto;

    public parentSites: SitesModel[];
    public selectedParentSite: SitesModel;

    public addressesToRemove: string[];
    public phonesToRemove: string[];
    public emailsToRemove: string[];

    public disableSave: boolean;

    private static minutesInDay = 1440;
    private static minutesInHour = 60;

    public static getDefault(parentSites: SitesModel[], selectedSite: Site): SiteCreateState {
        const s = new SiteCreateState();

        s.addresses = [Address.newAddress(ContactPointType.Work)];
        s.phones = [PhoneNumber.newPhone(ContactPointType.Work, PhoneType.Phone)];
        s.emails = [Email.newEmail(ContactPointType.Work)];
        s.localizedDisplayName = null;
        s.localizedWebsite = null;
        s.openingHours = [];
        s.specialties = [];
        s.offeredSpecialtyIds = [];
        s.localizedDescription = null;
        s.localizedOpeningHoursNote = null;
        s.localizedKioskInstruction = null;

        s.clinicalName = "";
        s.clinicalCode = "";
        s.aipLogin = "";
        s.acronym = "";
        s.ceLoginRAMQ = "";
        s.numberBadAccess = 3;
        s.multifactorAuthenticationNumberBadAccess = Site.defaultMultifactorAuthenticationNumberBadAccess;
        s.periodOfInactivityDays = 42;
        s.unapprovedNoteExpiryInMinutes = 20160; // 20160 = 2 weeks as the default value when creating new organizations
        s.unapprovedNoteMinutes = this.getUnapprovedNoteExpiryMinutes(s.unapprovedNoteExpiryInMinutes);
        s.unapprovedNoteHours = this.getUnapprovedNoteExpiryHours(s.unapprovedNoteExpiryInMinutes);
        s.unapprovedNoteDays = this.getUnapprovedNoteExpiryDays(s.unapprovedNoteExpiryInMinutes);
        s.isGmf = false;
        s.isGmfr = false;
        s.isGmfu = false;

        s.emailsToRemove = [];
        s.addressesToRemove = [];
        s.phonesToRemove = [];

        s.selectedParentSite = parentSites[0];

        if (selectedSite && selectedSite.Id !== GuidHelper.Empty()) {
            s.selectedParentSite = selectedSite.ParentSiteId ? parentSites.filter(s => s.Model.Id === selectedSite.ParentSiteId).firstOrDefault() : parentSites.filter(s => s.Model.Id === selectedSite.Id).firstOrDefault();
        }

        s.parentSites = parentSites;
        s.payeeNumber = "";
        s.mspFacilityNumber = "";

        const defaultTaxRates = AdministrationHelper.getTaxesForDefaultAddress(s.addresses);
        s.gst = defaultTaxRates?.GST ?? 0;
        s.qst = defaultTaxRates?.PST ?? 0;
        s.hst = defaultTaxRates?.HST ?? 0;
        s.genericTax = 0;
        return s;
    }

    public static fromSiteModel(s: SiteCreateState, site: Site, parentSites: SitesModel[]): SiteCreateState {
        s.addresses = MyleHelper.CloneObject(site.Adresses);
        s.addresses.forEach(a => {
            a.AvailableContactPointTypeLookupIds = cacheStore.GetContactPointTypeLookups();
            a.AvailableCountryLookupIds = cacheStore.GetCountryLookups();
            a.AvailableCountryRegionLookupIds = cacheStore.GetCountryRegionLookups(cacheStore.GetLookupById(a.CountryLookupId));

            a.Id = sessionStore.newId().toString();
        });
        if (!s.addresses.length) {
            s.addresses.push(Address.newAddress(ContactPointType.Work));
        }

        s.phones = MyleHelper.CloneObject(site.Phones);
        s.phones.forEach(p => {
            p.AvailableCountryLookupIds = cacheStore.GetCountryLookups();
            p.AvailablePhoneTypeLookupIds = cacheStore.GetPhoneContactPointTypeLookupTuples();
            p.renderId = sessionStore.newId().toString();
        });
        if (!s.phones.length) {
            s.phones.push(PhoneNumber.newPhone(ContactPointType.Work, PhoneType.Phone));
        }

        s.emails = MyleHelper.CloneObject(site.Emails);
        s.emails.forEach(e => {
            e.AvailableContactPointTypeLookupIds = cacheStore.GetContactPointTypeLookups();
            e.renderId = sessionStore.newId().toString();
        });

        if (!s.emails.length) {
            s.emails.push(Email.newEmail(ContactPointType.Work));
        }

        s.id = site.Id;
        s.clinicalName = site.ClinicalName;
        s.clinicalCode = site.ClinicalCode;
        s.aipLogin = site.AipLogin;
        s.acronym = site.Acronym;
        s.ceLoginRAMQ = site.CeLoginRamq;
        s.numberBadAccess = site.NumberBadAccess;
        s.multifactorAuthenticationNumberBadAccess = site.MultifactorAuthenticationNumberBadAccess;
        s.periodOfInactivityDays = site.PeriodOfInactivityDays;
        s.unapprovedNoteExpiryInMinutes = site.UnapproveNoteExpiryInMinutes || 0;
        s.unapprovedNoteMinutes = this.getUnapprovedNoteExpiryMinutes(s.unapprovedNoteExpiryInMinutes);
        s.unapprovedNoteHours = this.getUnapprovedNoteExpiryHours(s.unapprovedNoteExpiryInMinutes);
        s.unapprovedNoteDays = this.getUnapprovedNoteExpiryDays(s.unapprovedNoteExpiryInMinutes);
        s.gst = site.GstValue;
        s.qst = site.QstValue;
        s.hst = site.HstValue;
        s.genericTax = site.GenericTaxValue;
        s.isGmf = site.IsGMF;
        s.isGmfr = site.IsGMFR;
        s.isGmfu = site.IsGMFU;
        s.mspLocationCode = site.MspLocationCode;
        s.payeeNumber = site.PayeeNumber;
        s.mspFacilityNumber = site.MspFacilityNumber;
        s.localizedDisplayName = site.LocalizedDisplayName;
        s.localizedWebsite = site.LocalizedWebsite;
        s.openingHours = site.OpeningHours || [];
        s.specialties = site.Specialties || [];
        s.offeredSpecialtyIds = site.OfferedSpecialtyIds || [];
        s.localizedDescription = site.LocalizedDescription;
        s.localizedKioskInstruction = site.LocalizedKioskInstruction;
        s.localizedOpeningHoursNote = site.LocalizedOpeningHoursNote;

        s.emailsToRemove = [];
        s.addressesToRemove = [];
        s.phonesToRemove = [];

        parentSites.map(p => {
            if (p.Model.Id === site.ParentSiteId) {
                s.selectedParentSite = p;
            }
        });

        s.parentSites = parentSites;
        s.selectAsMainEnrollmentForClinicalCode = site.MainEnrolmentSiteForClinicalCode;

        return s;
    }

    public static getUnapprovedNoteExpiryMinutes(unapproveNoteExpiryInMinutes: number): number {
        const roundedDownDaysInMinutes = this.getUnapprovedNoteExpiryDays(unapproveNoteExpiryInMinutes) * this.minutesInDay;
        const roundedDownHoursInMinutes = this.getUnapprovedNoteExpiryHours(unapproveNoteExpiryInMinutes) * this.minutesInHour;
        return Math.floor(unapproveNoteExpiryInMinutes - roundedDownDaysInMinutes - roundedDownHoursInMinutes);
    }
    public static getUnapprovedNoteExpiryHours(unapproveNoteExpiryInMinutes: number): number {
        const roundedDownDaysInMinutes = this.getUnapprovedNoteExpiryDays(unapproveNoteExpiryInMinutes) * this.minutesInDay;
        return Math.floor((unapproveNoteExpiryInMinutes - roundedDownDaysInMinutes) / this.minutesInHour);
    }
    public static getUnapprovedNoteExpiryDays(unapproveNoteExpiryInMinutes: number): number {
        return Math.floor(unapproveNoteExpiryInMinutes / this.minutesInDay);
    }
}

class SiteCreate extends BaseReactComponent<ISiteCreateProps, SiteCreateState> {
    constructor(props: ISiteCreateProps) {
        super(props);

        let initialState = new SiteCreateState();
        const model = props.model.get("Model");
        const parentSites = props.model.get("ParentSites").toArray();

        if (model.Id === GuidHelper.Empty()) {
            initialState = SiteCreateState.getDefault(parentSites, props.selectedSite);
        }
        else {
            initialState = SiteCreateState.fromSiteModel(initialState, model, parentSites);
        }

        this.state = initialState;
    }

    public componentDidMount(): void {
        $(window).trigger("resize");
    }

    public componentDidUpdate(prevProps: ISiteCreateProps): void {
        if (this.props !== prevProps) {
            $(window).trigger("resize");

            if (IsNullOrWhiteSpace(prevProps.model.get("Model").ClinicalName) &&
            this.props.model.get("Id") !== prevProps.model.get("Id")
            ) {
                const selectedSite = this.state.selectedParentSite ? this.state.selectedParentSite.Model : prevProps.selectedSite;
                this.mutateState(s => SiteCreateState.getDefault(s.parentSites, selectedSite));
            }

            this.mutateState(s => s.disableSave = false);
        }
    }

    public render(): JSX.Element {
        const isFormValid = this.isSiteCreatingValid(this.props.model.get("IsOrganization"));
        const enableSitePortalInformation = cacheStore.ClinicSettings.EnableSitePortalInformation;
        const enableKioskCheckinMessage = cacheStore.ClinicSettings.EnableKioskMultiSiteCheckin;

        return (
            <div className="fxheight">
                <div className="site-banner">
                    <h3 className="banner-title">{cacheStore.GetString(this.props.titleLabel)}</h3>
                </div>
                <div className="fxheight scrollable" data-hvar={60}>
                    <SiteInfoSection
                        model={this.props.model}
                        infoLabel={this.props.infoLabel}
                        clinicalName={this.state.clinicalName}
                        selectAsMainEnrollmentForClinicalCode={this.state.selectAsMainEnrollmentForClinicalCode}
                        acronym={this.state.acronym}
                        clinicalCode={this.state.clinicalCode}
                        unapprovedNoteDays={this.state.unapprovedNoteDays}
                        unapprovedNoteHours={this.state.unapprovedNoteHours}
                        unapprovedNoteMinutes={this.state.unapprovedNoteMinutes}
                        ceLoginRAMQ={this.state.ceLoginRAMQ}
                        addresses={this.state.addresses}
                        aipLogin={this.state.aipLogin}
                        mspLocationCode={this.state.mspLocationCode}
                        mspFacilityNumber={this.state.mspFacilityNumber}
                        payeeNumber={this.state.payeeNumber}
                        parentSites={this.state.parentSites}
                        selectedParentSite={this.state.selectedParentSite}
                        isGmf={this.state.isGmf}
                        isGmfr={this.state.isGmfr}
                        isGmfu={this.state.isGmfu}
                        isUnapprovedNoteExpiryHoursValid={() => this.isUnapprovedNoteExpiryHoursValid()}
                        isUnapprovedNoteExpiryDaysValid={() => this.isUnapprovedNoteExpiryDaysValid()}
                        isUnapprovedNoteExpiryMinutesValid={() => this.isUnapprovedNoteExpiryMinutesValid()}
                        isMSPFacilityNumberValid={() => this.isMSPFacilityNumberValid()}
                        isPayeeNumberValid={() => this.isPayeeNumberValid()}
                        mutateState={props => this.mutateState(props)}
                    />
                    {!this.props.model.get("IsOrganization") && (
                        <div>
                            <AddressSection
                                addresses={this.state.addresses}
                                gst={this.state.gst}
                                qst={this.state.qst}
                                hst={this.state.hst}
                                mutateState={props => this.mutateState(props)}
                            />
                            <PhoneSection
                                phones={this.state.phones}
                                phonesToRemove={this.state.phonesToRemove}
                                mutateState={props => this.mutateState(props)}
                            />
                            <EmailSection
                                emails={this.state.emails}
                                emailsToRemove={this.state.emailsToRemove}
                                mutateState={props => this.mutateState(props)}
                            />
                            {enableSitePortalInformation && (
                                <InfoInPortalSection
                                    localizedDisplayName={this.state.localizedDisplayName}
                                    localizedDescription={this.state.localizedDescription}
                                    localizedWebsite={this.state.localizedWebsite}
                                    specialties={this.state.specialties}
                                    offeredSpecialtyIds={this.state.offeredSpecialtyIds}
                                    openingHours={this.state.openingHours}
                                    localizedOpeningHoursNote={this.state.localizedOpeningHoursNote}
                                    mutateState={this.mutateState.bind(this)}
                                />
                            )}
                            <SecuritySection
                                numberBadAccess={this.state.numberBadAccess}
                                multifactorAuthenticationNumberBadAccess={this.state.multifactorAuthenticationNumberBadAccess}
                                periodOfInactivityDays={this.state.periodOfInactivityDays}
                                mutateState={props => this.mutateState(props)}
                            />
                            <TaxesSection
                                addresses={this.state.addresses}
                                genericTax={this.state.genericTax}
                                gst={this.state.gst}
                                qst={this.state.qst}
                                hst={this.state.hst}
                                mutateState={props => this.mutateState(props)}
                            />
                            {enableKioskCheckinMessage && (
                                <KioskInstructionSection
                                    localizedKioskInstruction={this.state.localizedKioskInstruction}
                                    mutateState={props => this.mutateState(props)}
                                />
                            )}
                        </div>
                    )}

                </div>
                <div className="meta-footer">
                    <div className="flex-display flex-end">
                        <ButtonGroup spacing="sm">
                            <ButtonControl
                                className="btn btn-default"
                                onClick={() => this.onCancelClick()}
                                label={cacheStore.GetString("CancelLabel")}
                            />
                            <ButtonControl
                                variant="brand"
                                disabled={!isFormValid || this.state.disableSave}
                                onClick={() => this.onSaveClick()}
                                label={cacheStore.GetString("SaveLabel")}
                            />
                        </ButtonGroup>
                    </div>
                </div>
            </div>
        );
    }

    private onSaveClick(): void {
        this.mutateState(s => s.disableSave = true);

        sessionStore.dispatch(
            LocalOperations.Sites.SaveSite,
            { dto: this.fromSiteCreateState(this.state, this.props) }
        );
    }

    public fromSiteCreateState(state: SiteCreateState, props: ISiteCreateProps): ISiteDTO {
        const dto = {
            id: state.id,
            clinicalName: state.clinicalName,
            clinicalCode: state.clinicalCode,
            aipLogin: state.aipLogin,
            acronym: state.acronym,
            ceLoginRAMQ: state.ceLoginRAMQ,
            selectAsMainEnrollmentForClinicalCode: state.selectAsMainEnrollmentForClinicalCode,
            numberBadAccess: state.numberBadAccess,
            multifactorAuthenticationNumberBadAccess: state.multifactorAuthenticationNumberBadAccess,
            periodOfInactivityDays: state.periodOfInactivityDays,
            unapproveNoteExpiryInMinutes: state.unapprovedNoteExpiryInMinutes,

            gst: state.gst,
            qst: state.qst,
            hst: state.hst,
            genericTax: state.genericTax,

            isGmf: state.isGmf,
            isGmfr: state.isGmfr,
            isGmfu: state.isGmfu,

            addresses: state.addresses.filter(c => c.IsValid && !IsNullOrWhiteSpace(c.StreetAddress)),
            phoneNumbers: state.phones.filter(c => c.IsValid),
            emails: state.emails.filter(c => c.IsValid),
            localizedDisplayName: state.localizedDisplayName,
            localizedWebsite: state.localizedWebsite,
            specialties: state.specialties,
            offeredSpecialtyIds: state.offeredSpecialtyIds,
            openingHours: state.openingHours,
            localizedDescription: state.localizedDescription,
            localizedOpeningHoursNote: state.localizedOpeningHoursNote,
            localizedKioskInstruction: state.localizedKioskInstruction,

            addressesToRemove: state.addressesToRemove,
            phonesToRemove: state.phonesToRemove,
            emailsToRemove: state.emailsToRemove,

            parentSiteId: props.model.get("IsOrganization") ? null : state.selectedParentSite.Model.Id,

            mspLocationCode: state.mspLocationCode,
            payeeNumber: state.payeeNumber,
            mspFacilityNumber: state.mspFacilityNumber
        };
        return dto;
    }

    private onCancelClick(): void {
        sessionStore.dispatch(LocalOperations.Sites.CancelSite, { site: this.props.model.get("Model") });
    }

    private isClinicalNameValid(): boolean {
        return !IsNullOrWhiteSpace(this.state.clinicalName);
    }

    private isClinicalCodeValid(): boolean {
        return !IsNullOrWhiteSpace(this.state.clinicalCode);
    }

    private isAcronymValid(): boolean {
        return !IsNullOrWhiteSpace(this.state.acronym);
    }

    private isParentSiteValid(): boolean {
        return !IsNullOrWhiteSpace(this.state.selectedParentSite.Model.ClinicalName);
    }

    private isAddressEmpty(): boolean {
        return !(this.state.addresses.length === 0);
    }

    private areAddressesValid(): boolean {
        return this.state.addresses.every(a =>
            !IsNullOrWhiteSpace(a.StreetAddress)
            && !IsNullOrWhiteSpace(a.City)
            && !IsNullOrWhiteSpace(a.ZipCode)
            && a.IsValid
        );
    }

    private isPhoneEmpty(): boolean {
        return !(this.state.phones.length === 0);
    }

    private arePhonesValid(): boolean {
        return this.state.phones.every(p =>
            !IsNullOrWhiteSpace(p.Number)
            && PhoneNumber.validatePhone(p)
        );
    }

    private isNumberBadAccessValid(): boolean {
        return !IsNullOrWhiteSpace(this.state.numberBadAccess.toString()) && this.state.numberBadAccess > 0;
    }

    private isMultifactorAuthenticationNumberBadAccessValid(): boolean {
        return this.state.multifactorAuthenticationNumberBadAccess > 0;
    }

    private isMSPFacilityNumberValid(): boolean {
        return IsNullOrWhiteSpace(this.state.mspFacilityNumber) || this.state.mspFacilityNumber?.length <= 5;
    }

    private isPayeeNumberValid(): boolean {
        return IsNullOrWhiteSpace(this.state.payeeNumber) || this.state.payeeNumber?.length <= 5;
    }
    private areClinicWebsitesValid(): boolean {
        return AdministrationHelper.getLocalizedTextSiteCreateArray(this.state.localizedWebsite).every(p => ValidationHelper.validateUrl(p.value));
    }

    private isPeriodOfInactivityDaysValid(): boolean {
        return IsNullOrWhiteSpace(this.state.periodOfInactivityDays.toString()) || (this.state.periodOfInactivityDays > 0 && this.state.periodOfInactivityDays <= 999);
    }

    private isGstValid(): boolean {
        return !IsNullOrWhiteSpace(this.state.gst?.toString());
    }

    private isQstValid(): boolean {
        return !IsNullOrWhiteSpace(this.state.qst?.toString());
    }

    private isUnapprovedNoteExpiryDaysValid(): boolean {
        return /^\d*$/gi.test(this.state.unapprovedNoteDays.toString());
    }

    private isUnapprovedNoteExpiryHoursValid(): boolean {
        return /^\d{0,2}$/gi.test(this.state.unapprovedNoteHours.toString());
    }

    private isUnapprovedNoteExpiryMinutesValid(): boolean {
        return /^[012345]?\d?$/gi.test(this.state.unapprovedNoteMinutes.toString());
    }

    private isSiteCreatingValid(isOrganization: boolean): boolean {
        if (isOrganization) {
            return this.isClinicalNameValid() &&
                this.isClinicalCodeValid() &&
                this.isAcronymValid() &&
                this.isUnapprovedNoteExpiryDaysValid() &&
                this.isUnapprovedNoteExpiryHoursValid() &&
                this.isUnapprovedNoteExpiryMinutesValid();
        }

        return this.isClinicalNameValid() &&
            this.isClinicalCodeValid() &&
            this.isAcronymValid() &&
            this.isParentSiteValid() &&
            this.isAddressEmpty() &&
            this.areAddressesValid() &&
            this.isPhoneEmpty() &&
            this.arePhonesValid() &&
            this.isNumberBadAccessValid() &&
            this.isMultifactorAuthenticationNumberBadAccessValid() &&
            this.isPeriodOfInactivityDaysValid() &&
            this.isGstValid() &&
            this.isQstValid() &&
            this.isMSPFacilityNumberValid() &&
            this.isPayeeNumberValid() &&
            this.areClinicWebsitesValid();
    }
}

export { SiteCreateState, SiteGmfType };
export default SiteCreate;
